import { Fragment } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { isPhase1DestinationEnabled } from "../../config/appScope";

/**
 * Low-emphasis links to user/role admin screens (tenant dashboard footer).
 */
export default function AdminQuickLinks() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSuperAdmin = user?.roles?.includes("super_admin");
  const isTenantAdmin = user?.roles?.includes("TenantAdmin");
  const canUsers =
    isSuperAdmin ||
    isTenantAdmin ||
    user?.permissions?.includes("manage_users");
  const canRoles =
    isSuperAdmin || user?.permissions?.includes("manage_roles");

  /** @type {{ label: string, path: string }[]} */
  const links = [];
  if (canUsers && isPhase1DestinationEnabled("/admin/users")) {
    links.push({ label: "Users", path: "/admin/users" });
  }
  if (canUsers && isPhase1DestinationEnabled("/admin/invites")) {
    links.push({ label: "Invites", path: "/admin/invites" });
  }
  if (canRoles && isPhase1DestinationEnabled("/admin/roles")) {
    links.push({ label: "Roles", path: "/admin/roles" });
  }
  if (canRoles && isPhase1DestinationEnabled("/admin/roles/create")) {
    links.push({ label: "New role", path: "/admin/roles/create" });
  }

  if (links.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 3,
        pt: 2,
        borderTop: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.75, letterSpacing: 0.3 }}
      >
        Organization and team
      </Typography>
      <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={0.5}>
        {links.map((l, i) => (
          <Fragment key={l.path}>
            {i > 0 ? (
              <Typography
                component="span"
                variant="caption"
                color="text.disabled"
                sx={{ userSelect: "none" }}
              >
                ·
              </Typography>
            ) : null}
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={() => navigate(l.path)}
              sx={{
                textTransform: "none",
                minWidth: 0,
                px: 0.5,
                color: "text.secondary",
                fontWeight: 500,
                "&:hover": { color: "primary.main", bgcolor: "transparent" },
              }}
            >
              {l.label}
            </Button>
          </Fragment>
        ))}
      </Stack>
    </Box>
  );
}
